/**
 * @file serviceStep.component.tsx
 * @path apps/website/src/features/booking/components/serviceStep.component.tsx
 * @description Service selection step with collapsible pricing options (legacy UI).
 */

import { Service } from "@sirbastians/types";
import { Button, Input } from "@sirbastians/ui";
import { cn } from "@sirbastians/utils";
import { CheckCircle2, ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useBookingStore } from "../booking.store";
import { services } from "@/data/services";

export const ServiceStep = () => {
  const selectedService = useBookingStore((s) => s.selectedService);
  const setService = useBookingStore((s) => s.setService);
  const showValidation = useBookingStore((s) => s.showValidation);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [isExpanded, setIsExpanded] = useState(true);

  const categories = ["All", "Transport", "Tours"];

  const filteredServices = useMemo(
    () =>
      (services as Service[]).filter((s) => {
        if (s.status !== "active") return false;
        if (s.slug === "uwc-cput-student-shuttle") return false;
        if (filterCategory !== "All" && s.category !== filterCategory) return false;
        if (searchQuery && !s.title.toLowerCase().includes(searchQuery.toLowerCase()))
          return false;
        return true;
      }),
    [searchQuery, filterCategory],
  );

  // Force expand when service changes
  useEffect(() => {
    if (selectedService) setIsExpanded(true);
  }, [selectedService]);

  const onServiceClick = (service: Service) => {
    if (selectedService?.slug === service.slug) {
      setIsExpanded((prev) => !prev);
    } else {
      setService(service);
    }
  };

  const hasError = showValidation && !selectedService;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="text-left space-y-2">
        <h2 className="text-2xl font-bold">Choose Your Service</h2>
        <p className="text-muted-foreground">
          Select a transfer or tour to view pricing options.
        </p>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-secondary/20 p-3 rounded-lg">
        <div className="relative w-full sm:w-96 border border-border rounded-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search services..."
            className="pl-9 h-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={filterCategory === cat ? "primary" : "ghost"}
              onClick={() => setFilterCategory(cat)}
              size="sm"
              className="h-9"
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Service List */}
      <div
        data-field-id="service-selector"
        className={cn(
          "space-y-3",
          hasError && "p-3 border-2 border-destructive/50 rounded-xl bg-destructive/5",
        )}
      >
        {filteredServices.map((service) => {
          const isSelected = selectedService?.slug === service.slug;
          const showDetails = isSelected && isExpanded;

          return (
            <div
              key={service.slug}
              onClick={() => onServiceClick(service)}
              className={cn(
                "group rounded-xl border transition-all cursor-pointer overflow-hidden select-none",
                isSelected
                  ? "border-accent bg-accent/5 ring-1 ring-accent"
                  : "border-border hover:border-accent/50 bg-card",
              )}
            >
              {/* Main Row */}
              <div className="p-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <h3
                        className={cn(
                          "font-semibold text-base sm:text-lg transition-colors line-clamp-1",
                          isSelected ? "text-primary" : "text-foreground",
                        )}
                      >
                        {service.title}
                      </h3>
                      {isSelected && (
                        <CheckCircle2 className="h-5 w-5 text-accent animate-in fade-in zoom-in" />
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed line-clamp-1">
                      {service.shortDescription}
                    </p>
                  </div>

                  <div className="text-right shrink-0 flex flex-col items-end">
                    <span className="text-xs text-muted-foreground block uppercase tracking-wider">
                      From
                    </span>
                    <span className="font-bold text-lg text-accent">
                      R{service.pricing?.amount?.toLocaleString() ?? "—"}
                    </span>
                    {isSelected && (
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-primary mt-2 transition-transform duration-300",
                          showDetails && "rotate-180",
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Pricing Options */}
              <div
                className={cn(
                  "grid transition-all duration-300 ease-in-out",
                  showDetails
                    ? "grid-rows-[1fr] opacity-100 border-t border-accent/20"
                    : "grid-rows-[0fr] opacity-0",
                )}
              >
                <div className="overflow-hidden">
                  <div className="p-4 pt-4 bg-muted/30">
                    <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Pricing Options
                    </div>
                    <div>
                      {service.pricing?.options?.map(
                        (
                          option: { label: string; price: string; description?: string },
                          idx: number,
                        ) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-xs p-2 rounded text-foreground hover:text-accent transition-colors"
                          >
                            <span className="font-medium">{option.label}</span>
                            <div className="text-right">
                              <span className="font-bold block">{option.price}</span>
                              {option.description && (
                                <span className="text-xs text-muted-foreground">
                                  {option.description}
                                </span>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {hasError && (
        <p className="text-sm text-destructive font-medium text-center">
          Please select a service to continue.
        </p>
      )}

      {filteredServices.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No services match your search.
        </p>
      )}
    </div>
  );
};